import React, { useState, useEffect } from 'react';
import { Bot, Loader2, ShieldAlert, TrendingUp, AlertTriangle, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { apiPost, apiGet } from '../data/api';

const VERDICT_CONFIG = {
  TAKE: { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', icon: CheckCircle, label: 'PRENDI' },
  CAUTION: { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', icon: AlertTriangle, label: 'CAUTELA' },
  SKIP: { color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: XCircle, label: 'SKIP' },
  ERROR: { color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/30', icon: ShieldAlert, label: 'ERRORE' },
  UNKNOWN: { color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/30', icon: Bot, label: '?' },
};

export function AIAdvisorMatch({ matchId }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cached, setCached] = useState(null);

  useEffect(() => {
    apiGet(`/tt/admin/ai-advisor/${encodeURIComponent(matchId)}`)
      .then(d => { if (d && d.verdict) setCached(d); })
      .catch(() => {});
  }, [matchId]);

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const res = await apiPost(`/tt/admin/ai-advisor/${encodeURIComponent(matchId)}`);
      if (res && res.verdict) {
        setAnalysis(res);
        setCached(null);
      } else if (res && res.error) {
        setAnalysis({ verdict: 'ERROR', analysis: res.error, risk_flags: [] });
      }
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('524') || msg.includes('timeout')) {
        setAnalysis({ verdict: 'ERROR', analysis: 'Server timeout — il match richiede troppi dati. Riprova tra poco.', risk_flags: [] });
      } else {
        setAnalysis({ verdict: 'ERROR', analysis: msg, risk_flags: [] });
      }
    } finally {
      setLoading(false);
    }
  };

  const data = analysis || cached;
  const verdict = data?.verdict || 'UNKNOWN';
  const cfg = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.UNKNOWN;
  const Icon = cfg.icon;

  return (
    <div data-testid="ai-advisor-section" className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-violet-400" />
          <h3 className="text-lg font-bold text-white">AI Advisor</h3>
          <span className="text-xs text-zinc-500 ml-1">Claude Sonnet</span>
        </div>
        <button
          data-testid="ai-advisor-analyze-btn"
          onClick={handleAnalyze}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
          {loading ? 'Analisi in corso...' : data ? 'Rianalizza' : 'Chiedi all\'AI'}
        </button>
      </div>

      {!data && !loading && (
        <p className="text-zinc-500 text-sm">Clicca "Chiedi all'AI" per ottenere un'analisi dettagliata di questo match.</p>
      )}

      {loading && (
        <div className="flex items-center gap-3 py-8 justify-center">
          <Loader2 size={20} className="animate-spin text-violet-400" />
          <span className="text-zinc-400">L'AI sta analizzando tutti i dati del match...</span>
        </div>
      )}

      {data && !loading && (
        <div className="space-y-4">
          {/* Verdict Banner */}
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${cfg.bg}`}>
            <Icon size={28} className={cfg.color} />
            <div>
              <div className={`text-xl font-bold ${cfg.color}`}>{cfg.label}</div>
              {data.confidence > 0 && (
                <div className="text-xs text-zinc-400 mt-0.5">Confidence AI: {data.confidence}/10</div>
              )}
            </div>
            {data.best_market && data.best_market !== 'none' && (
              <span className="ml-auto px-3 py-1 rounded-full bg-zinc-800 text-xs text-zinc-300 border border-zinc-700">
                Mercato migliore: {data.best_market === 'match_winner' ? 'MW' : data.best_market === 'handicap' ? 'HC' : data.best_market === 'over_under' ? 'O/U' : data.best_market}
              </span>
            )}
          </div>

          {/* Key Insight */}
          {data.key_insight && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
              <TrendingUp size={16} className="text-violet-400 mt-0.5 shrink-0" />
              <p className="text-sm text-violet-200">{data.key_insight}</p>
            </div>
          )}

          {/* Analysis */}
          {data.analysis && (
            <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">
              {data.analysis}
            </div>
          )}

          {/* O/U and HC Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.ou_analysis && (
              <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-1">Over/Under</h4>
                <p className="text-sm text-zinc-300">{data.ou_analysis}</p>
              </div>
            )}
            {data.hc_analysis && (
              <div className="p-3 rounded-lg bg-orange-500/5 border border-orange-500/20">
                <h4 className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-1">Handicap</h4>
                <p className="text-sm text-zinc-300">{data.hc_analysis}</p>
              </div>
            )}
          </div>

          {/* Risk Flags */}
          {data.risk_flags && data.risk_flags.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Risk Flags</h4>
              {data.risk_flags.map((flag, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <ShieldAlert size={13} className="text-red-400 shrink-0" />
                  <span className="text-red-300">{flag}</span>
                </div>
              ))}
            </div>
          )}

          {/* Stake Suggestion */}
          {data.stake_suggestion && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
              <TrendingUp size={16} className="text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <span className="text-xs text-zinc-400 font-semibold uppercase">Stake Suggestion</span>
                <p className="text-sm text-zinc-300 mt-0.5">{data.stake_suggestion}</p>
              </div>
            </div>
          )}

          {/* Timestamp + stale warning */}
          {data.created_at && (() => {
            const ageMin = Math.round((Date.now() - new Date(data.created_at).getTime()) / 60000);
            const isStale = ageMin > 30;
            return (
              <div className={`text-xs text-right ${isStale ? 'text-amber-500' : 'text-zinc-600'}`}>
                {isStale && <span>Dati potrebbero essere cambiati — clicca Rianalizza | </span>}
                Analisi: {data.created_at.replace('T', ' ').slice(0, 16)} ({ageMin < 60 ? `${ageMin}min fa` : `${Math.round(ageMin/60)}h fa`})
              </div>
            );
          })()}
        </div>
      )}

      {data?.error && (
        <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/20">
          Errore: {data.error}
        </div>
      )}
    </div>
  );
}
